import React, { useRef, useEffect, useState } from "react";
import { useScrollAnimation } from "../hooks/useScrollAnimation";
import { useTranslation } from "../hooks/useTranslation";

const data = [
  {
    img: "/assets/icons/tradingproducts/1.png",
    title: "ROUND BARS",
    description:
      "Versatile solid bars used in construction, machining, and fabrication. Strong, durable, and easy to process  ",
  },
  {
    img: "/assets/icons/tradingproducts/2.png",
    title: "IPE / IPN",
    description:
      "Precision-engineered steel beams for strong, reliable frameworks — available with parallel or tapered flanges",
  },
  {
    img: "/assets/icons/tradingproducts/3.png",
    title: "UPE / UPN",
    description:
      "UPE has parallel flanges, while UPN features tapered ones , ideal for frames, supports, and structural applications.",
  },
  {
    img: "/assets/icons/tradingproducts/4.png",
    title: "L EQUAL / UNEQUAL",
    description:
      "Strong steel angles with equal or unequal legs — ideal for bracing, framing, and structural reinforcements",
  },
  {
    img: "/assets/icons/tradingproducts/5.png",
    title: "FLATS",
    description:
      "Durable flat bars used in construction, machinery, and general fabrication. Strong, reliable, and easy to work with",
  },
  {
    img: "/assets/icons/tradingproducts/6.png",
    title: "HEA/HEB",
    description:
      "HEA beams are lighter with wide flanges, while HEB beams are heavier and stronger—perfect for columns and high-load structures",
  },
  {
    img: "/assets/icons/tradingproducts/8.png",
    title: "SQUARE BARS",
    description:
      "Square bars offer uniform strength in all directions, making them ideal for machining, structural components, and general fabrication  ",
  },
];

const extendedData = [...data, ...data];

function TradingProducts() {
  const sliderRef = useRef(null);
  const isPointerDown = useRef(false);
  const startX = useRef(0);
  const scrollStart = useRef(0);
  const isPausedRef = useRef(false);
  const animationRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  const [titleRef, titleVisible] = useScrollAnimation();
  const [descriptionRef, descriptionVisible] = useScrollAnimation();
  const [sliderContainerRef, sliderContainerVisible] = useScrollAnimation();
  const { t } = useTranslation();

  const wrapScrollPosition = () => {
    const slider = sliderRef.current;
    if (!slider) return;
    const maxScroll = slider.scrollWidth / 2;
    if (slider.scrollLeft >= maxScroll) {
      slider.scrollLeft -= maxScroll;
    } else if (slider.scrollLeft <= 0) {
      slider.scrollLeft += maxScroll;
    }
  };

  useEffect(() => {
    const slider = sliderRef.current;
    if (!slider) return;

    const animate = () => {
      if (!isPausedRef.current) {
        slider.scrollLeft += 0.6;
        wrapScrollPosition();
      }
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleWindowPointerUp = (event) => {
      if (!isPointerDown.current) return;
      finishDrag(event);
    };

    window.addEventListener("pointerup", handleWindowPointerUp);
    return () => window.removeEventListener("pointerup", handleWindowPointerUp);
  }, []);

  const startDrag = (event) => {
    const slider = sliderRef.current;
    if (!slider) return;
    event.preventDefault();
    isPointerDown.current = true;
    isPausedRef.current = true;
    setIsDragging(true);
    startX.current = event.clientX;
    scrollStart.current = slider.scrollLeft;
    slider.setPointerCapture?.(event.pointerId);
  };

  const onDrag = (event) => {
    const slider = sliderRef.current;
    if (!slider || !isPointerDown.current) return;
    const deltaX = event.clientX - startX.current;
    slider.scrollLeft = scrollStart.current - deltaX;
    wrapScrollPosition();
  };

  const finishDrag = (event) => {
    const slider = sliderRef.current;
    if (!slider || !isPointerDown.current) return;
    slider.releasePointerCapture?.(event.pointerId);
    isPointerDown.current = false;
    setIsDragging(false);
    setTimeout(() => {
      if (!isPointerDown.current) {
        isPausedRef.current = false;
      }
    }, 150);
  };

  const handlePointerLeave = (event) => {
    if (isPointerDown.current) {
      finishDrag(event);
    }
  };

  return (
    <div className="tradingproducts-wrapper">
      <div className="tradingproducts">
        <h2
          ref={titleRef}
          className={`fs-900 clr-dark ${titleVisible ? "animate-in" : ""}`}
        >
          {t("tradingProducts.title")}
        </h2>
        <p
          ref={descriptionRef}
          className={`ff-inter fs-500 ${
            descriptionVisible ? "animate-in" : ""
          }`}
        >
          {t("tradingProducts.description")}
        </p>
        <div
          ref={sliderContainerRef}
          className={`tradingproducts-slider-container ${
            sliderContainerVisible ? "animate-in" : ""
          }`}
        >
          <div
            ref={sliderRef}
            className={`tradingproducts-list ${
              isDragging ? "is-dragging" : ""
            }`}
            onPointerDown={startDrag}
            onPointerMove={onDrag}
            onPointerUp={finishDrag}
            onPointerLeave={handlePointerLeave}
          >
            <div className="tradingproducts-track">
              {extendedData.map((item, index) => (
                <div
                  className="tradingproducts-card"
                  key={`${item.title}-${index}`}
                >
                  <img src={item.img} alt={item.title} />
                  <h3 className="clr-white fs-600">{item.title}</h3>
                  <p className="clr-white fs-300">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TradingProducts;
